/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication
 * Redirects to login if not authenticated
 * Only renders children when authenticated
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchAuthSession, getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { CircularProgress, Box } from '@mui/material';
import { useBusiness } from '../contexts/BusinessContext';

interface ProtectedRouteProps {
  children: (props: { user: any; signOut: () => Promise<void> }) => React.ReactNode;
  redirectTo?: string;
  isAdminRoute?: boolean; // Skip business selection for Mission Control
}

export default function ProtectedRoute({ 
  children, 
  redirectTo = '/login',
  isAdminRoute = false
}: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Use the global business context
  const { selectedVendorId, hasNoBusinesses, loading: businessLoading } = useBusiness();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        if (idToken) {
          const currentUser = await getCurrentUser();
          setIsAuthenticated(true);
          setUser(currentUser);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
      window.location.href = redirectTo;
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e27',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} replace />;
  }

  // For authenticated users, wait for business context to load
  // (Unless it's an admin route which doesn't need business context)
  if (!isAdminRoute && businessLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e27',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Only render protected content if:
  // 1. We have a selected vendor, OR
  // 2. User has no businesses (Dashboard will show appropriate message), OR
  // 3. This is an admin route (Mission Control doesn't need business context)
  if (!isAdminRoute && !selectedVendorId && !hasNoBusinesses) {
    // This state implies we are authenticated, business context loaded, but no selection and has businesses.
    // The Context should have auto-selected or set hasNoBusinesses.
    // If we get here, something might be weird, but let's show loader or fallback.
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e27',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children({ user, signOut: handleSignOut })}</>;
}


